import posthog, { type CaptureResult } from "posthog-js";
import type { OnboardingStep } from "@/services/onboardingService";
import type { User } from "@/types/user";
import type { AnalyticsSource } from "@/types/analytics";

const KEY = import.meta.env.VITE_POSTHOG_KEY as string;
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string) || "https://app.posthog.com";
const ONBOARDING_MAX_STEP_KEY = "posthogOnboardingMaxStep";
const EXTERNAL_SCANNER_REJECTION =
  /^Non-Error promise rejection captured with value: Object Not Found Matching Id:[1-4], MethodName:update, ParamCount:4$/;

type TrackedOnboardingStep = Exclude<OnboardingStep, "signup">;

const ONBOARDING_STEPS: Record<
  TrackedOnboardingStep,
  { step: number; step_name: string }
> = {
  domain: { step: 1, step_name: "domain" },
  billing: { step: 2, step_name: "plan_checkout" },
  buyer: { step: 3, step_name: "buyer_profile" },
  questions: { step: 4, step_name: "buyer_questions" },
  simulation: { step: 5, step_name: "buyer_run" },
  complete: { step: 6, step_name: "results_dashboard" },
};

export function filterKnownExternalErrors(
  event: CaptureResult | null,
): CaptureResult | null {
  if (event?.event !== "$exception") return event;

  const exceptionList = event.properties.$exception_list;
  if (!Array.isArray(exceptionList) || exceptionList.length !== 1) return event;

  const exception = exceptionList[0];
  if (
    typeof exception !== "object" ||
    exception === null ||
    !("type" in exception) ||
    !("value" in exception)
  ) {
    return event;
  }

  return exception.type === "UnhandledRejection" &&
    typeof exception.value === "string" &&
    EXTERNAL_SCANNER_REJECTION.test(exception.value)
    ? null
    : event;
}

export function initPosthog() {
  if (!KEY) return;
  posthog.init(KEY, {
    api_host: HOST,
    autocapture: false,
    // Pageviews are tracked manually in PageViewTracker to avoid duplicates in SPA navigation.
    capture_pageview: false,
    capture_pageleave: true,
    before_send: filterKnownExternalErrors,
  });
}

export function identifyOnAuth(user: User) {
  posthog.identify(String(user.id), {
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    $created_at: user.created_ts,
  });
}

export function setWorkspaceAnalyticsContext(params: {
  workspace_id: number | string;
  billing_account_id: number | string;
}) {
  posthog.register({
    workspace_id: String(params.workspace_id),
    billing_account_id: String(params.billing_account_id),
  });
}

export function clearWorkspaceAnalyticsContext() {
  posthog.unregister("workspace_id");
  posthog.unregister("billing_account_id");
}

export function resetUser() {
  posthog.reset();
}

const getHighestOnboardingStep = (userId: number, currentStep: number): number => {
  try {
    const storageKey = `${ONBOARDING_MAX_STEP_KEY}:${userId}`;
    const storedStep = Number(localStorage.getItem(storageKey));
    const highestStep = Number.isInteger(storedStep)
      ? Math.max(storedStep, currentStep)
      : currentStep;
    localStorage.setItem(storageKey, String(highestStep));
    return highestStep;
  } catch {
    return currentStep;
  }
};

export function trackOnboardingStepViewed(params: {
  user_id?: number;
  step: TrackedOnboardingStep;
  company_id?: number | string | null;
  company_name?: string | null;
}) {
  const step = ONBOARDING_STEPS[params.step];
  const highestStep = params.user_id == null
    ? step.step
    : getHighestOnboardingStep(params.user_id, step.step);

  posthog.capture("onboarding_step_viewed", {
    step: step.step,
    step_name: step.step_name,
    ...(params.company_id != null ? { company_id: String(params.company_id) } : {}),
    ...(params.company_name ? { company_name: params.company_name } : {}),
    $set: { onboarding_step: highestStep },
  });
}

export function trackCheckoutStarted(params: {
  cta: "start_trial" | "subscribe_monthly" | "subscribe_annual";
  link_variant: "monthly_10d" | "monthly_30d" | "monthly_direct" | "annual";
  plan: "v2_monthly" | "v2_annual";
  billing_interval: "monthly" | "annual";
  company_id?: number | string | null;
  company_name?: string | null;
}) {
  const { company_id, company_name, ...eventProperties } = params;
  posthog.capture("checkout_started", {
    ...eventProperties,
    ...(company_id != null ? { company_id: String(company_id) } : {}),
    ...(company_name ? { company_name } : {}),
    source: "onboarding_v2",
  });
}

export function trackReportViewed(params: {
  report_id: number | string;
  company_id?: number | string | null;
  company_name?: string | null;
  overall_score?: number | string | null;
}) {
  posthog.capture("report_viewed", {
    report_id: String(params.report_id),
    ...(params.company_id != null ? { company_id: String(params.company_id) } : {}),
    company_name: params.company_name,
    overall_score: params.overall_score,
  });
}

export function trackReportShared(params: {
  report_id: number | string;
  share_method: "email" | "link" | "social" | string;
  recipient_count?: number;
  company_id?: number | string | null;
  company_name?: string | null;
}) {
  posthog.capture("report_shared", {
    report_id: String(params.report_id),
    share_method: params.share_method,
    recipient_count: params.recipient_count,
    ...(params.company_id != null ? { company_id: String(params.company_id) } : {}),
    ...(params.company_name ? { company_name: params.company_name } : {}),
  });
}

export function trackWorkspaceSwitched(params: {
  workspace_id: number | string;
  previous_workspace_id?: number | string | null;
  workspace_name?: string | null;
  user_role?: string | null;
}) {
  const activeWorkspaceId = String(params.workspace_id);

  posthog.capture("workspace_switched", {
    workspace_id: activeWorkspaceId,
    previous_workspace_id:
      params.previous_workspace_id != null
        ? String(params.previous_workspace_id)
        : null,
    workspace_name: params.workspace_name ?? null,
    user_role: params.user_role ?? null,
    $set: {
      active_workspace_id: activeWorkspaceId,
      user_role: params.user_role ?? null,
    },
  });
}

// ---------------------------------------------------------------------------
// Virtual Buyer lifecycle (AITS-545). `created`, `questions_saved` and
// `run_started` fire from the shared VB mutation hooks (so in-app + onboarding
// are both covered once); onboarding threads `source: "onboarding_v2"`, in-app
// omits `source`.
//
// The run TERMINALS (`virtual_buyer_run_completed` / `virtual_buyer_run_failed`)
// are NOT emitted here — they moved to the backend (buyer/services/run_events.py),
// fired when the analysis reaches a terminal state. A run takes ~2-3 min and the
// user routinely navigates away before it finishes (most acutely in onboarding),
// so a client that never witnesses the finish can't emit it — completions were
// silently dropping. The backend always witnesses the finalize. `run_started`
// stays client-side (the user is right there clicking "Run").
// ---------------------------------------------------------------------------

export type BuyerOrigin = "suggested" | "edited" | "custom";

/** Buyer provenance for `virtual_buyer_created`. No Marcus suggestion ⇒
 *  hand-built ("custom"); a suggestion the user changed — either a persona field
 *  OR any of the generated questions — ⇒ "edited"; a suggestion taken as-is ⇒
 *  "suggested". Evaluated when the buyer is finalized (first run), so post-
 *  generate persona tweaks and question edits both count (AITS-545 QA). */
export function resolveBuyerOrigin(params: {
  suggestedSnapshot: unknown;
  personaEdited: boolean;
  questionsEdited: boolean;
}): BuyerOrigin {
  if (params.suggestedSnapshot == null) return "custom";
  return params.personaEdited || params.questionsEdited ? "edited" : "suggested";
}

// Fire `virtual_buyer_created` once per buyer per app session — it's emitted at
// buyer finalization (first run), so a retried run trigger can't double-count it.
const createdBuyers = new Set<number>();

export function trackVirtualBuyerCreated(params: {
  buyerId: number;
  origin: BuyerOrigin;
  companyId?: number | string | null;
  companyName?: string | null;
  /** Current buyer total (person property); omit when unknown. */
  virtualBuyerCount?: number | null;
  source?: AnalyticsSource;
}) {
  if (createdBuyers.has(params.buyerId)) return;
  createdBuyers.add(params.buyerId);
  posthog.capture("virtual_buyer_created", {
    buyer_id: String(params.buyerId),
    buyer_origin: params.origin,
    ...(params.companyId != null ? { company_id: String(params.companyId) } : {}),
    ...(params.companyName ? { company_name: params.companyName } : {}),
    ...(params.source ? { source: params.source } : {}),
    ...(params.virtualBuyerCount != null
      ? { $set: { virtual_buyer_count: params.virtualBuyerCount } }
      : {}),
  });
}

export function trackVirtualBuyerQuestionsSaved(params: {
  buyerId: number;
  questionsCount: number;
  source?: AnalyticsSource;
}) {
  posthog.capture("virtual_buyer_questions_saved", {
    buyer_id: String(params.buyerId),
    questions_count: params.questionsCount,
    ...(params.source ? { source: params.source } : {}),
  });
}

/** Current calendar month as "YYYY-MM" (VB runs have no first-class run month;
 *  it's the month the run was kicked off). Reads `Date.now()` so tests can pin it. */
const currentRunMonth = (): string => new Date(Date.now()).toISOString().slice(0, 7);

export function trackVirtualBuyerRunStarted(params: {
  buyerId: number;
  questionsCount?: number | null;
  source?: AnalyticsSource;
}) {
  posthog.capture("virtual_buyer_run_started", {
    buyer_id: String(params.buyerId),
    ...(params.questionsCount != null ? { questions_count: params.questionsCount } : {}),
    run_month: currentRunMonth(),
    ...(params.source ? { source: params.source } : {}),
  });
}

// `virtual_buyer_run_completed` / `virtual_buyer_run_failed` used to live here.
// They now fire server-side (buyer/services/run_events.py) on finalize — see the
// section header above — so a run that finishes after the user has navigated away
// still emits its terminal event. Nothing client-side to guard or dedup anymore.

// ---------------------------------------------------------------------------
// MarcusAI Workbench (AITS-547). `workbench_viewed` fires frontend-side because
// it's a page render (like `report_viewed`) and the GET it would otherwise hang
// off is also used by the sidebar badge — so the backend can't tell a real visit
// from a badge poll. `fix_marked_done` is emitted backend-side instead (it needs
// the user's all-time completion tally for is_first_fix / fixes_completed_count);
// see workbench/views.py. The page controls firing frequency (once per company
// view) via a ref, so this stays a dumb capture like the other *_viewed events.
// ---------------------------------------------------------------------------

export function trackWorkbenchViewed(params: {
  companyId: number;
  fixesRemaining: number;
  fixesDone: number;
  companyName?: string | null;
}) {
  posthog.capture("workbench_viewed", {
    company_id: String(params.companyId),
    ...(params.companyName ? { company_name: params.companyName } : {}),
    fixes_remaining: params.fixesRemaining,
    fixes_done: params.fixesDone,
  });
}

export { posthog };
